<template>
  <div>
    <form @submit.prevent="onSubmit()">
      <div class="card">
        <div class="bg-dark card-header text-white">Academic Indirect Cost Distribution - Certify &amp; Close</div>
        <div class="card-body">
          <AppLoadingContainer :predicate="isLoading">

            <div>
              <ul style="list-style: none;">
                <li>
                  <AppCheckBox :disabled="isSubmitting || !canEdit" v-model="payPeriod.certifiedData">
                    I certify all Academic Indirect Cost Distribution calculations and Fund Transfer entries have been reviewed and reconciled
                  </AppCheckBox>
                </li>
                <li>
                  <AppCheckBox :disabled="isSubmitting || missingAttachmetType || !canEdit" v-model="payPeriod.certifiedReports">
                    I certify the following reports for the current month have been reviewed, verified and uploaded:
                  </AppCheckBox>
                  <ul style="list-style: none;">
                    <li v-for="attachment in attachments" :key="attachment.id"><span style="font-size: larger;" :style="attachment.uploaded ? 'color: green;' : 'color: red;'">
                      {{ attachment.uploaded | checked }}</span> {{ attachment.attachmentType | enumName(attachmentTypeEnum) }}: {{ attachment.uploaded ? attachment.fileName : "MISSING" }}
                    </li>
                  </ul>
                </li>
                <li>
                  <AppCheckBox :disabled="isSubmitting || !canEdit" v-model="payPeriod.certifiedPartiesNotified">
                    I certify that the requests for fund transfers report have been electronically sent to the Financial Services
                  </AppCheckBox>
                </li>
              </ul>
            </div>

            <div>Certified By: {{ payPeriod.certifiedByPersonName }}</div>
            <div>Certification Date: 
              <span v-if="payPeriod.certifiedDate !== null">{{ payPeriod.certifiedDate | moment('MM/DD/YYYY') }}</span>
              <span v-if="payPeriod.certifiedDate === null && canEdit">{{ new Date() | moment('MM/DD/YYYY') }}</span>
            </div>
          <AppLoadingContainer :predicate="isLoading">
        </div>
        <div v-if="canEdit" class="card-footer d-print-none text-right">
          <button :disabled="isSubmitting" @click.prevent="onSubmit(false)" class="btn btn-primary" type="submit">Save</button>
          <button :disabled="isSubmitting" @click.prevent="isClosing = true;" class="btn btn-primary" type="submit">Certify &amp; Close</button>
        </div>
      </div>
    </form>
    
    <AppModalConfirm v-if="canEdit" :visible="isClosing" @close="isClosing = false;" @confirm="onSubmit(true)">
        <template v-slot:header>Confirm</template>
        <AppAlertNote v-if="canEdit">
          Once submitted, the Status will be set to Completed and no further alterations to the records will be allowed
        </AppAlertNote>
        <p>Are you sure you want to certify and close for the pay period of {{ payPeriodStartDateDisplay }}?</p>
    </AppModalConfirm>
  </div>
</template>

<script>
import AppAlertNote from "@/components/shared/AppAlertNote";
import AppLoadingContainer from "@/components/shared/AppLoadingContainer";
import AcademicIndirectCostService from "../../services/AcademicIndirectCostService";
import { AttachmentType } from "@/services/models/AcademicIndirectCost"
import { checked } from "@/filters/Boolean";
import { enumName } from "@/filters/Enum";
import AppCheckBox from '../shared/AppCheckBox.vue';
import EnumExtensions from '@/utilities/extensions/EnumExtensions';
import {ToastType} from "@/services/models/shared/ToastType";
import AppModalConfirm from "@/components/shared/AppModalConfirm";

export default {
    components: {
      AppLoadingContainer,
      AppCheckBox,
      AppAlertNote,
      AppModalConfirm
    },
    filters: {
      checked,
      enumName
    },
    props: {
      fiscalYear: {
        required: true,
        type: Number
      },
      payPeriodId: {
        required: true,
        type: String
      },
      payPeriodStartDate: {
        required: true,
        type: Date
      },
      canEdit: {
        required: true,
        type: Boolean
      },
      payPeriodStartDateDisplay: {
        required: true,
        type: String
      }
    },
    data() {
        return {
          isLoading: false,
          payPeriod: {
            id: null,
            certifiedData: false,
            certifiedReports: false,
            certifiedPartiesNotified: false,
            isCompleted: false,
            certifiedByPersonName: null,
            certifiedByPersonId: null,
            certifiedDate: null
          },
          attachments: [],
          isSubmitting: false,
          isClosing: false
        }
    },
    computed: {
      isClosed() {
        return this.payPeriod !== null || this.payPeriod.certifiedData && this.payPeriod.certifiedReports && this.payPeriod.certifiedPartiesNotified;
      },
      
      attachmentTypeEnum() {
        return AttachmentType;
      },

      attachmentTypes() {
        return EnumExtensions.EnumToSelectList(AttachmentType);
      },

      missingAttachmetType() {
        return this.attachments.filter((obj) => !obj.uploaded).length > 0
      }
    },
    methods: {
      /**
       * @description Loads the data from the API
       */
      loadData() {
        this.isLoading = true;

        AcademicIndirectCostService.getPayPeriod(this.payPeriodId)
            .then(response => {
                this.payPeriod = response;
                if (response.certifiedByPersonName === null && this.canEdit) this.payPeriod.certifiedByPersonName = this.$store.state.user.name;
                AcademicIndirectCostService.getAttachmentList(this.payPeriodId)
                    .then(innerResopnse => {
                      if (innerResopnse && innerResopnse.length > 0) {
                        //Add missing types
                        this.attachmentTypes.forEach(item => {
                          const index = innerResopnse.findIndex(obj => obj.attachmentType === item.value);
                          if (index === -1) {
                            this.attachments.push({
                              attachmentType: item.value, 
                              id: null, 
                              fileName: null, 
                              createdByPersonName: null, 
                              createdDate: null, 
                              uploaded: false
                            });
                          } else {
                            this.attachments.push({
                              attachmentType: innerResopnse[index].attachmentType, 
                              id: innerResopnse[index].id, 
                              fileName: innerResopnse[index].fileName, 
                              createdByPersonName: innerResopnse[index].createdByPersonName, 
                              createdDate: innerResopnse[index].createdDate, 
                              uploaded: true
                            })
                          }
                        });
                      } else {
                        this.attachments = this.attachmentTypes.map(item => { return { attachmentType: item.value, id: null, fileName: null, createdByPersonName: null, createdDate: null, uploaded: false }; }); 
                      }
                    })
                    .finally(() => {
                        this.isLoading = false;
                    })
            })
            .catch(() => {
                this.redirectToError()
            })
      },
      
      onSubmit(close) {
        this.isSubmitting = true;
        this.payPeriod.certifiedByPersonId = this.$store.state.user.id;
        this.payPeriod.isCompleted = close;
        this.payPeriod.payPeriodId = this.payPeriodId;
        AcademicIndirectCostService.updatePayPeriod(this.payPeriod)
          .then(() => {
            this.alert(ToastType.Success, close ? 'Academic Indirect Cost Distribution was certified and closed successfully' : 'Academic Indirect Cost Distribution checklist saved successfully');
            if (close) this.$router.replace({ name: 'PayPeriods' });
          })
          .catch(() => {
            this.alert(ToastType.Danger, close ? 'Failed to save Academic Indirect Cost Distribution certification' : 'Failed to save Academic Indirect Cost Distribution checklist');
          })
          .finally(() => {
            this.isSubmitting = false;
          });
      }
    },
    created() {
      this.loadData();
    }
}
</script>
